/**
 * Resilient asset URL resolver — pick a CDN-hosted URL when reachable,
 * silently fall back to a self-hosted copy when the CDN is blocked
 * (corporate firewall) or down (CDN outage).
 *
 * Usage:
 *   import { resolveAssetUrl } from "@/lib/assetLoader";
 *
 *   // Synchronous: pass both URLs, returns the CDN URL immediately
 *   // (good first guess) and probes asynchronously in the background.
 *   // If the CDN fails, future reads of the same key resolve to the
 *   // local URL.
 *
 *   const url = await resolveAssetUrl("pdf-worker", cdnUrl, localUrl);
 *
 * The probe runs once per (cdnUrl) across the page lifetime, so cost is
 * a single HEAD request per CDN asset. Results are memoized.
 */

const _verdicts = new Map(); // cdnUrl → Promise<"cdn" | "local">

function _probe(cdnUrl) {
  if (_verdicts.has(cdnUrl)) return _verdicts.get(cdnUrl);
  const p = fetch(cdnUrl, { method: "HEAD", mode: "cors" })
    .then((r) => (r.ok ? "cdn" : "local"))
    .catch(() => "local");
  _verdicts.set(cdnUrl, p);
  return p;
}

/**
 * Resolve to the CDN URL when reachable, the local URL otherwise.
 * Awaits the HEAD probe so callers always get a known-good URL.
 */
export async function resolveAssetUrl(_key, cdnUrl, localUrl) {
  if (typeof window === "undefined" || typeof fetch !== "function") return cdnUrl;
  const verdict = await _probe(cdnUrl);
  if (verdict === "local" && localUrl) {
    // eslint-disable-next-line no-console
    console.warn(`[assetLoader] CDN unreachable, using local: ${localUrl}`);
    return localUrl;
  }
  return cdnUrl;
}

/**
 * Fire-and-forget variant used when the caller can accept an optimistic
 * CDN URL up-front and only needs the fallback for *future* reads. Returns
 * the CDN URL immediately; the probe still runs in the background and
 * subsequent `resolveAssetUrl()` calls for the same `cdnUrl` will return
 * the local URL when appropriate.
 */
export function resolveAssetUrlOptimistic(_key, cdnUrl, localUrl) {
  if (typeof window === "undefined" || typeof fetch !== "function") return cdnUrl;
  _probe(cdnUrl); // kick off probe; ignore result here
  return cdnUrl;
}
